class AcciCart {
  constructor() {
    this.drawer = document.getElementById('acci-cart-drawer');
    const cartPage = document.getElementById('acci-cart-page-inner');
    if (!this.drawer && !cartPage) return;

    this.bindEvents();
    this.bindViewport();

    // Listen for custom events triggered by product pages or other components
    document.addEventListener('cart:open', () => this.open());
    document.addEventListener('cart:updated', () => this.refresh());
    
    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.refresh();
        this.open();
      });
    }
  }

  bindEvents() {
    // Delegation for close buttons and overlay
    document.addEventListener('click', (e) => {
      if (e.target.hasAttribute('data-cart-close')) {
        this.close();
      }
      if (e.target.hasAttribute('data-cart-open') || e.target.closest('[data-cart-open]')) {
        e.preventDefault();
        this.open();
      }
      const removeBtn = e.target.closest('[data-cart-remove]');
      if (removeBtn) {
        const key = removeBtn.getAttribute('data-cart-remove');
        this.removeItem(key);
      }
    });

    // Delegation for quantity changes via input
    document.addEventListener('change', (e) => {
      if (e.target.hasAttribute('data-cart-qty-input')) {
        const key = e.target.getAttribute('data-cart-qty-input');
        const qty = parseInt(e.target.value, 10) || 0;
        this.changeQuantity(key, qty);
      }
    });

    // Delegation for quantity changes via buttons
    document.addEventListener('click', (e) => {
      const adjustBtn = e.target.closest('[data-cart-qty-adjust]');
      if (adjustBtn) {
        const key = adjustBtn.getAttribute('data-cart-qty-key');
        const change = parseInt(adjustBtn.getAttribute('data-cart-qty-adjust'), 10);
        // Find the corresponding input in the same container (drawer or page)
        const container = adjustBtn.closest('.acci-cart-drawer__inner');
        if (container) {
          const input = container.querySelector(`[data-cart-qty-input="${key}"]`);
          if (input) {
            const newQty = Math.max(0, parseInt(input.value, 10) + change);
            input.value = newQty;
            this.changeQuantity(key, newQty);
          }
        }
      }
    });

    // Delegate all cart links to open the drawer instead
    document.body.addEventListener('click', (e) => {
      const link = e.target.closest('a');
      if (link && !link.classList.contains('no-drawer')) {
        const cartUrl = window.routes ? window.routes.cart_url : '/cart';
        let pathname = link.pathname;
        if (!pathname.startsWith('/')) pathname = '/' + pathname;
        pathname = pathname.replace(/\/$/, '');
        const targetUrl = cartUrl.replace(/\/$/, '');
        if (pathname === targetUrl || pathname === '/cart') {
          e.preventDefault();
          this.open();
        }
      }
    });
  }

  bindViewport() {
    if (!this.drawer) return;

    this.syncViewport = () => {
      if (this.viewportFrame) cancelAnimationFrame(this.viewportFrame);
      this.viewportFrame = requestAnimationFrame(() => {
        this.viewportFrame = null;
        if (!this.drawer) return;
        const height = window.visualViewport ? window.visualViewport.height : window.innerHeight;
        this.drawer.style.setProperty('--cart-drawer-height', `${height}px`);
      });
    };

    this.syncViewport();
    window.addEventListener('resize', this.syncViewport);
    window.addEventListener('orientationchange', this.syncViewport);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.syncViewport);
    }
  }

  open() {
    if (this.drawer) {
      this.syncViewport();
      this.drawer.classList.add('is-open');
      document.body.classList.add('fade-global');
      document.body.style.overflow = 'hidden';
    }
  }

  close() {
    if (!this.drawer) return;
    if (this.drawer.classList.contains('is-closing')) return;

    this.drawer.classList.add('is-closing');
    // Remove is-open immediately so the inner panel can animate back out.
    this.drawer.classList.remove('is-open');
    document.body.classList.remove('fade-global');
    document.body.style.overflow = '';
    if (this.drawer.contains(document.activeElement) && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }

    // Let the inner panel animate back out before hiding the container.
    const durationMs = 700;
    setTimeout(() => {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-closing');
    }, durationMs);
  }

  async removeItem(key) {
    const itemEls = document.querySelectorAll(`[data-cart-item][data-key="${key}"]`);
    if (itemEls.length > 0) {
      itemEls.forEach(el => el.classList.add('is-removing'));
    } else {
      if (this.drawer) this.drawer.classList.add('is-loading');
      const cartPage = document.getElementById('acci-cart-page-inner');
      if (cartPage) cartPage.classList.add('is-loading');
    }

    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });
      this.refresh();
    } catch (error) {
      console.error('Error removing item:', error);
      if (itemEls.length > 0) itemEls.forEach(el => el.classList.remove('is-removing'));
      if (this.drawer) this.drawer.classList.remove('is-loading');
      const cartPage = document.getElementById('acci-cart-page-inner');
      if (cartPage) cartPage.classList.remove('is-loading');
    }
  }

  async changeQuantity(key, quantity) {
    const itemEls = document.querySelectorAll(`[data-cart-item][data-key="${key}"]`);
    if (itemEls.length > 0) {
      itemEls.forEach(el => el.classList.add('is-updating'));
    } else {
      if (this.drawer) this.drawer.classList.add('is-loading');
      const cartPage = document.getElementById('acci-cart-page-inner');
      if (cartPage) cartPage.classList.add('is-loading');
    }

    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      this.refresh();
    } catch (error) {
      console.error('Error updating quantity:', error);
      if (itemEls.length > 0) itemEls.forEach(el => el.classList.remove('is-updating'));
      if (this.drawer) this.drawer.classList.remove('is-loading');
      const cartPage = document.getElementById('acci-cart-page-inner');
      if (cartPage) cartPage.classList.remove('is-loading');
    }
  }

  async refresh() {
    try {
      const hasCartPage = document.getElementById('acci-cart-page-inner') !== null;
      let sectionsToFetch = 'cart-drawer,acci-header,header';
      if (hasCartPage) {
        sectionsToFetch += ',acci-main-cart';
      }

      // Use Shopify Section Rendering API
      const res = await fetch(`${window.location.pathname}?sections=${sectionsToFetch}`);
      const data = await res.json();

      // Update Cart Drawer
      if (data['cart-drawer'] && this.drawer) {
        const parser = new DOMParser();
        const html = parser.parseFromString(data['cart-drawer'], 'text/html');
        const newDrawer = html.getElementById('acci-cart-drawer');
        if (newDrawer) {
          this.drawer.innerHTML = newDrawer.innerHTML;
        }
      }

      // Update Cart Page if it exists
      if (hasCartPage && data['acci-main-cart']) {
        const parser = new DOMParser();
        const html = parser.parseFromString(data['acci-main-cart'], 'text/html');
        const newCartPage = html.getElementById('acci-cart-page-inner');
        const currentCartPage = document.getElementById('acci-cart-page-inner');
        if (newCartPage && currentCartPage) {
          currentCartPage.innerHTML = newCartPage.innerHTML;
        }
      }

      // Update Header Cart Count
      const headerHtml = data['acci-header'] || data['header'];
      if (headerHtml) {
        const parser = new DOMParser();
        const html = parser.parseFromString(headerHtml, 'text/html');
        const newBubble = html.getElementById('cart-icon-bubble');
        const currentBubbles = document.querySelectorAll('#cart-icon-bubble');
        if (newBubble && currentBubbles.length > 0) {
          currentBubbles.forEach(bubble => {
            bubble.innerHTML = newBubble.innerHTML;
          });
        }
      }
    } catch (error) {
      console.error('Error refreshing cart:', error);
    } finally {
      if (this.drawer) this.drawer.classList.remove('is-loading');
      const cartPage = document.getElementById('acci-cart-page-inner');
      if (cartPage) cartPage.classList.remove('is-loading');
    }
  }
}

function initAcciCart() {
  if (!window.AcciCartInstance) {
    window.AcciCartInstance = new AcciCart();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAcciCart);
} else {
  initAcciCart();
}
